/**
 * Iter 250 — AI Coach Panel for the Live Translator.
 *
 * Pendant l'appel, l'agent peut cliquer "Demander à l'IA Coach" : GPT-5.2 analyse
 * la conversation récente et propose 2-3 suggestions bilingues :
 *   - "agent_native" : à lire mentalement (FR si l'agent parle FR)
 *   - "agent_translated" : à dire au contact (EN si le contact parle EN)
 *
 * 1 clic sur une suggestion → met le texte dans le champ de texte du traducteur
 * (qui pourra l'envoyer ou le lire à voix haute via TTS).
 */
import { useState } from "react";
import { Button } from "../ui/button";
import { Brain, Loader2, ChevronDown, ChevronUp, Volume2, Send, Sparkles } from "lucide-react";
import { toast } from "sonner";

const STYLES = [
  { value: "balanced", label: "⚖️ Équilibré", desc: "Écoute + progression" },
  { value: "consultative", label: "🤝 Consultatif", desc: "Questions qualifiantes" },
  { value: "aggressive", label: "🎯 Closing", desc: "Demande l'engagement" },
];

export default function CoachPanel({ api, session, onUseSuggestion, onSpeakSuggestion }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [style, setStyle] = useState("balanced");
  const [result, setResult] = useState(null);

  const ask = async () => {
    setLoading(true);
    try {
      const r = await api.post("/aircall/translator/coach/suggestions", {
        session_id: session.session_id,
        coach_style: style,
      });
      setResult(r.data);
      setOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Coach IA indisponible");
    } finally { setLoading(false); }
  };

  // Iter 251 — Log feedback when a suggestion is used or spoken aloud
  const sendFeedback = async (index, action) => {
    if (!result?.coach_log_id) return;
    try {
      await api.post("/aircall/translator/coach/feedback", {
        coach_log_id: result.coach_log_id,
        suggestion_index: index,
        action,
      });
    } catch (e) { /* silent */ }
  };

  const intentColor = {
    objection: "text-rose-400 bg-rose-500/10 border-rose-500/30",
    question: "text-cyan-400 bg-cyan-500/10 border-cyan-500/30",
    interest: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
    closing_window: "text-amber-400 bg-amber-500/10 border-amber-500/30",
    risk: "text-rose-400 bg-rose-500/10 border-rose-500/30",
  }[result?.detected_intent] || "text-slate-300 bg-slate-500/10 border-slate-500/30";

  return (
    <div className="bg-gradient-to-br from-purple-500/5 to-blue-500/5 border border-purple-500/20 rounded-xl p-4 space-y-3" data-testid="coach-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center">
            <Brain className="w-4 h-4" />
          </div>
          <div>
            <div className="text-sm font-bold text-white flex items-center gap-1.5">
              AI Coach <Sparkles className="w-3 h-3 text-purple-400" />
            </div>
            <div className="text-[11px] text-slate-400">
              Suggestions de réponses en temps réel pour le commercial
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={style}
            onChange={(e) => setStyle(e.target.value)}
            className="bg-[#0b0811] border border-white/10 rounded px-2 py-1 text-xs text-white"
            data-testid="coach-style"
          >
            {STYLES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <Button
            onClick={ask}
            disabled={loading}
            size="sm"
            className="bg-purple-500 hover:bg-purple-400 text-white"
            data-testid="coach-ask"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Brain className="w-3.5 h-3.5 mr-1.5" />}
            {loading ? "Analyse…" : "Demander au coach"}
          </Button>
        </div>
      </div>

      {result && (
        <div className="space-y-2.5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[10px] uppercase px-2 py-0.5 rounded border font-bold ${intentColor}`}>
                {result.detected_intent || "—"}
              </span>
              <span className="text-[11px] text-slate-400">
                {result.exchanges_count} échanges analysés
              </span>
            </div>
            <button onClick={() => setOpen(!open)} className="text-slate-400 hover:text-white" data-testid="coach-toggle">
              {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          </div>
          {open && (
            <>
              {result.context_summary && (
                <div className="text-xs text-slate-200 bg-white/[0.02] border border-white/5 rounded p-2.5">
                  <div className="text-[9px] uppercase text-slate-500 mb-1">Contexte</div>
                  {result.context_summary}
                </div>
              )}
              {(result.suggestions || []).length === 0 ? (
                <div className="text-xs text-slate-400 text-center py-3">
                  Pas de suggestion pertinente — continuez la conversation.
                </div>
              ) : (
                <div className="space-y-2">
                  {result.suggestions.map((sug, i) => (
                    <div key={i} className="bg-[#14101c] border border-white/10 rounded p-2.5 space-y-2" data-testid={`coach-sug-${i}`}>
                      <div className="text-[10px] uppercase text-purple-300 font-bold">{sug.label || `Suggestion ${i + 1}`}</div>
                      <div className="space-y-1.5">
                        <div className="text-xs text-slate-200">
                          <span className="text-[9px] text-slate-500 uppercase mr-1.5">{(session.source_lang || "?").toUpperCase()}</span>
                          {sug.agent_native}
                        </div>
                        <div className="text-xs text-cyan-200 italic border-l-2 border-cyan-500/30 pl-2">
                          <span className="text-[9px] text-cyan-500 uppercase mr-1.5">{(session.target_lang || "?").toUpperCase()}</span>
                          {sug.agent_translated}
                        </div>
                      </div>
                      <div className="flex gap-1.5 pt-1">
                        <Button
                          onClick={() => { onUseSuggestion?.(sug.agent_translated || sug.agent_native); sendFeedback(i, "used"); }}
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-[10px]"
                          data-testid={`coach-use-${i}`}
                        >
                          <Send className="w-3 h-3 mr-1" /> Utiliser
                        </Button>
                        {onSpeakSuggestion && (
                          <Button
                            onClick={() => { onSpeakSuggestion?.(sug.agent_translated, session.target_lang); sendFeedback(i, "spoken"); }}
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-[10px] text-cyan-400 border-cyan-500/30"
                            data-testid={`coach-speak-${i}`}
                          >
                            <Volume2 className="w-3 h-3 mr-1" /> Dire
                          </Button>
                        )}
                        <Button
                          onClick={() => { sendFeedback(i, "dismissed"); toast.info("Suggestion ignorée"); }}
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-[10px] text-slate-500 hover:text-slate-300"
                          data-testid={`coach-dismiss-${i}`}
                        >
                          Ignorer
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
